import { Button, InlineStack, Tooltip } from '@customerio/pluma-components/react';
import { Step, type MergeConfirmations } from '../types';

const AGREE_TOOLTIP = 'Agree to the above before continuing.';

type MergeActionsProps = {
  activeStep: Step;
  /** The viewer may edit people. */
  canMerge: boolean;
  hasRequiredSetup: boolean;
  isPreviewing: boolean;
  isMerging: boolean;
  confirmations: MergeConfirmations;
  cancelHref: string;
  onNext: () => void;
  onBack: () => void;
  onMerge: () => void;
};

export function allConfirmed({ segments, history, irreversible }: MergeConfirmations): boolean {
  return segments && history && irreversible;
}

/** The wizard's footer: Cancel/Next on the setup step, Back/Confirm and merge on the review step. */
export function MergeActions({
  activeStep,
  canMerge,
  hasRequiredSetup,
  isPreviewing,
  isMerging,
  confirmations,
  cancelHref,
  onNext,
  onBack,
  onMerge,
}: MergeActionsProps) {
  if (activeStep === Step.Setup) {
    return (
      <InlineStack alignBlock="stretch" alignInline="end" gap="150">
        <Button variant="tertiary" isDisabled={isPreviewing} href={cancelHref} data-test="people-merge-button-cancel">
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!canMerge || !hasRequiredSetup || isPreviewing}
          onClick={onNext}
          data-test="people-merge-button-preview"
        >
          Next
        </Button>
      </InlineStack>
    );
  }

  const isConfirmed = allConfirmed(confirmations);
  return (
    <InlineStack alignBlock="stretch" alignInline="end" gap="150">
      <Button variant="tertiary" onClick={onBack} data-test="people-merge-button-back">
        Back
      </Button>
      <Tooltip content={AGREE_TOOLTIP} placement="top" isDisabled={isPreviewing || isConfirmed} isCustom>
        {(context) => (
          <Button
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            variant="primary"
            isDisabled={!canMerge || !hasRequiredSetup || isMerging || isPreviewing || !isConfirmed}
            onClick={onMerge}
            data-test="people-merge-button-merge"
          >
            {isMerging ? 'Merging…' : 'Confirm and merge'}
          </Button>
        )}
      </Tooltip>
    </InlineStack>
  );
}
